import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Target, Zap, Shield, Heart, Briefcase, Award } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
// PAGE 1: Title
export function Page1() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center" data-id="element-329">
      <motion.div variants={fadeUp} className="inline-block mb-6" data-id="element-330">
        <span className="px-4 py-1.5 rounded-full bg-green-50 text-sierra-blue text-sm font-medium tracking-wide" data-id="element-331">
          CANDIDATE PROFILE
        </span>
      </motion.div>
      <motion.h1 variants={fadeUp} className="text-5xl md:text-7xl font-bold tracking-tight text-sierra-dark leading-[1.1] mb-6" data-id="element-332">
        Diran George
      </motion.h1>
      <motion.p variants={fadeUp} className="text-xl md:text-2xl text-sierra-gray font-light mb-16 max-w-3xl mx-auto" data-id="element-333">
        Principal Enterprise Sales Engineering | London
      </motion.p>

      <motion.div variants={fadeUp} className="mt-24 pt-8 border-t border-gray-100 flex flex-col items-center max-w-2xl mx-auto" data-id="element-334">
        <p className="text-lg font-medium text-sierra-dark mb-2" data-id="element-335">
          Reduce contact centre cost while protecting CSAT (4.62)
        </p>
        <p className="text-sm text-gray-500" data-id="element-336">
          A high-trust AI agent for device setup, eSIM activation, and support
          triage.
        </p>
      </motion.div>
    </motion.div>;
}
// PAGE 2: Agenda + Demo Contract
export function Page2() {
  const agendaItems = [{
    time: '10m',
    title: 'You & Alignment',
    desc: "Why I'm here and how I work."
  }, {
    time: '10m',
    title: 'Owned Deal Review',
    desc: 'De-risking complex enterprise deals.'
  }, {
    time: '20m',
    title: 'ACME Live Demo',
    desc: 'eSIM activation hero journey.'
  }, {
    time: '5m',
    title: 'Closing',
    desc: '90-day plan & why Sierra.'
  }, {
    time: '5m',
    title: 'Q&A',
    desc: 'Open discussion.'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto w-full" data-id="element-337">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-12 text-center" data-id="element-338">
        Agenda & Demo
      </motion.h2>

      <motion.div variants={fadeUp} className="space-y-8 bg-white p-10 rounded-3xl sierra-shadow border border-gray-100" data-id="element-339">
        <h3 className="text-lg font-semibold text-sierra-gray mb-8 uppercase tracking-wider text-sm text-center" data-id="element-340">
          Today's Flow
        </h3>
        <div className="relative border-l-2 border-gray-100 pl-8 space-y-10 max-w-xl mx-auto" data-id="element-341">
          {agendaItems.map((item, i) => <div key={i} className="relative" data-id="element-342">
              <div className="absolute -left-[41px] top-1 w-4 h-4 rounded-full bg-white border-4 border-sierra-blue" data-id="element-343" />
              <div className="flex items-baseline gap-6" data-id="element-344">
                <span className="text-sm font-bold text-sierra-blue w-12 shrink-0" data-id="element-345">
                  {item.time}
                </span>
                <div data-id="element-346">
                  <h4 className="font-semibold text-sierra-dark text-lg" data-id="element-347">
                    {item.title}
                  </h4>
                  <p className="text-gray-500 mt-1" data-id="element-348">{item.desc}</p>
                </div>
              </div>
            </div>)}
        </div>
      </motion.div>
    </motion.div>;
}
// PAGE 3: Who I am
export function Page3() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-349">
      <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-sierra-dark leading-tight mb-12 border-l-4 border-sierra-blue pl-6" data-id="element-350">
        "I'm a customer-facing builder who turns business problems into clear
        technical motions — unlocking complexity and overcoming deal lethargy."
      </motion.h2>

      <motion.div variants={fadeUp} className="space-y-6 bg-white p-8 rounded-2xl sierra-shadow border border-gray-100" data-id="element-351">
        <div className="flex items-start gap-4" data-id="element-352">
          <Briefcase className="w-6 h-6 text-sierra-blue shrink-0 mt-1" data-id="element-353" />
          <div data-id="element-354">
            <h4 className="font-semibold text-lg" data-id="element-355">Enterprise SE Leadership</h4>
            <p className="text-gray-600 mt-1" data-id="element-356">
              Principal, Enterprise Sales Engineering at Attentive (EMEA scope).
              Previously Solution Consulting Manager at Treasure Data and Senior
              SE/SC at Salesforce, Sailthru, Selligent.
            </p>
          </div>
        </div>
        <div className="w-full h-px bg-gray-100" data-id="element-357" />
        <div className="flex items-start gap-4" data-id="element-358">
          <Zap className="w-6 h-6 text-amber-500 shrink-0 mt-1" data-id="element-359" />
          <div data-id="element-360">
            <h4 className="font-semibold text-lg" data-id="element-361">Builder DNA</h4>
            <p className="text-gray-600 mt-1" data-id="element-362">
              Founder of two bootstrapped SaaS products (Quickblog + LinkDeal).
              I understand product mechanics and go-to-market from the ground
              up.
            </p>
          </div>
        </div>
        <div className="w-full h-px bg-gray-100" data-id="element-363" />
        <div className="flex items-start gap-4" data-id="element-364">
          <Target className="w-6 h-6 text-emerald-500 shrink-0 mt-1" data-id="element-365" />
          <div data-id="element-366">
            <h4 className="font-semibold text-lg" data-id="element-367">Commercial Execution</h4>
            <p className="text-gray-600 mt-1" data-id="element-368">
              Strong blend of technical depth + commercial execution across
              complex enterprise buying committees.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
// PAGE 4: Signature strengths
export function Page4() {
  const strengths = [{
    icon: <Target className="w-8 h-8 text-blue-500" data-id="element-369" />,
    title: 'Complex Deal Execution',
    desc: 'Navigating RFPs, orchestrating migrations, and driving multi-stakeholder alignment in high-stakes enterprise environments.'
  }, {
    icon: <Zap className="w-8 h-8 text-amber-500" data-id="element-370" />,
    title: 'Technical Storytelling',
    desc: 'Building high-craft demos that connect deep technical capabilities to clear business narratives, proof, and ROI.'
  }, {
    icon: <Award className="w-8 h-8 text-emerald-500" data-id="element-371" />,
    title: 'Repeatability & Enablement',
    desc: 'Creating onboarding best practices, standardisation, and playbooks. Coaching teams to scale success beyond individual heroics.'
  }, {
    icon: <Target className="w-8 h-8 text-green-600" data-id="element-372" />,
    title: 'Value Engineering',
    desc: 'Building compelling business cases and ROI models.'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-7xl mx-auto w-full" data-id="element-373">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-12 text-center" data-id="element-374">
        Signature Strengths
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-375">
        {strengths.map((s, i) => <motion.div key={i} variants={fadeUp} className="bg-white p-8 rounded-2xl sierra-shadow border border-gray-100 hover:shadow-lg transition-shadow duration-300 flex flex-col" data-id="element-376">
            <div className="mb-6 p-3 bg-green-50 inline-block rounded-xl w-fit" data-id="element-377">
              {s.icon}
            </div>
            <h3 className="text-xl font-semibold mb-3" data-id="element-378">{s.title}</h3>
            <p className="text-gray-600 leading-relaxed flex-1" data-id="element-379">{s.desc}</p>
          </motion.div>)}
      </div>
    </motion.div>;
}
// PAGE 5: Proof metrics
export function Page5() {
  const metrics = [{
    value: '$1M',
    label: 'New product sales conversion driven in 5 months (AI & Email).'
  }, {
    value: '+12%',
    label: 'Improved win rate (25% to 37%) by attaching SE to the right opps.'
  }, {
    value: '-30%',
    label: 'Reduced time to launch via onboarding best practices & standardisation.'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-5xl mx-auto w-full" data-id="element-380">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-12 text-center" data-id="element-381">
        Measurable Impact
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-382">
        {metrics.map((m, i) => <motion.div key={i} variants={fadeUp} className="bg-white p-10 rounded-3xl sierra-shadow border border-gray-100 text-center relative overflow-hidden group" data-id="element-383">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-384" />
            <div className="relative z-10" data-id="element-385">
              <div className="text-5xl font-bold text-sierra-blue mb-4 tracking-tight" data-id="element-386">
                {m.value}
              </div>
              <p className="text-gray-600 font-medium leading-relaxed" data-id="element-387">
                {m.label}
              </p>
            </div>
          </motion.div>)}
      </div>
    </motion.div>;
}
// PAGE 6: Why Sierra
export function Page6() {
  const values = [{
    icon: <Shield className="w-5 h-5" data-id="element-388" />,
    name: 'Trust',
    desc: 'Safe, reliable solutions; honest trade-offs.'
  }, {
    icon: <Heart className="w-5 h-5" data-id="element-389" />,
    name: 'Customer Obsession',
    desc: 'Outcomes-first, reducing friction.'
  }, {
    icon: <Award className="w-5 h-5" data-id="element-390" />,
    name: 'Craftsmanship',
    desc: 'Demo polish + operational detail.'
  }, {
    icon: <Zap className="w-5 h-5" data-id="element-391" />,
    name: 'Intensity',
    desc: 'Momentum without chaos.'
  }, {
    icon: <Target className="w-5 h-5" data-id="element-392" />,
    name: 'Family',
    desc: 'Sustainable high performance.'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-393">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-12 text-center" data-id="element-394">
        Appendix: Values Aligned
      </motion.h2>
      <motion.div variants={fadeUp} className="bg-white rounded-2xl sierra-shadow border border-gray-100 overflow-hidden" data-id="element-395">
        {values.map((v, i) => <div key={i} className={`flex items-center p-6 ${i !== values.length - 1 ? 'border-b border-gray-100' : ''} hover:bg-gray-50 transition-colors`} data-id="element-396">
            <div className="w-12 h-12 rounded-full bg-blue-50 text-sierra-blue flex items-center justify-center shrink-0 mr-6" data-id="element-397">
              {v.icon}
            </div>
            <div data-id="element-398">
              <h3 className="text-lg font-semibold text-sierra-dark" data-id="element-399">
                {v.name}
              </h3>
              <p className="text-gray-600 mt-1" data-id="element-400">{v.desc}</p>
            </div>
          </div>)}
      </motion.div>
    </motion.div>;
}